import { Button } from '@/components/Button';
import { Modal, FormModal } from '@/components/Modal';
import { Typography } from '@/components/Typography';
import { contractTypeCodes } from '@/hooks/useJbpQuery';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBookingResponse200 } from '../../../../../APIDocs/apiTypes';
import { FormWorkflowView } from '@portal_components/formWorkflow/FormWorkflowView';
import { jobCommentsNode, siteAddressNode, siteContactDetailsNode } from '../GenericNodes';
import { Title } from '@portal_components/components/Title';
import { IndicatorType } from '@portal_components/components/WorkflowIndicator';
import { StateAddressDetails, StateContactDetails } from '@/reducers/jbpGeneralSlice';
import axios from 'axios';
import { EnvironmentVariables } from '@/EnvironmentVariables';
import { useAppSelector } from '@/store';
import { FormButton } from '@portal_components/components/FormButton';

interface EditJobFormValues {
  siteAddress: StateAddressDetails;
  siteContactDetails: StateContactDetails;
  jobComments: string;
}

interface FormEditJobProps {
  bookingId: string;
  mpan: string;
  contractId: string;
  serviceLine: string;
  booking: getBookingResponse200;
}

export const FormEditJob = ({ bookingId, mpan, contractId, serviceLine, booking }: FormEditJobProps) => {
  const queryClient = useQueryClient();
  const nav = useNavigate();
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [showUpdateWorkflow, setShowUpdateWorkflow] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { site, userAssociatedMpids } = useAppSelector(state => state.jbp);

  const onEditJobSuccess = () => {
    setShowLoadingModal(false);
    setShowSuccessModal(true);
  };

  const onEditJobError = () => {
    setShowLoadingModal(false);
    setShowErrorModal(true);
  };

  const modalTitle = 'Edit Job';

  const handleJobSuccess = () => {
    setShowSuccessModal(false);
    queryClient.invalidateQueries({ queryKey: [`site-${mpan}-${serviceLine}-${contractId}`] });
    queryClient.invalidateQueries({ queryKey: [`get-booking-list-${mpan}-${serviceLine}`] });
    nav(`/site/${mpan}/${serviceLine}`);
  };

  const handSubmit = async (values: EditJobFormValues) => {
    const formattedValues = {
      SiteDetails: {
        Title: values.siteContactDetails.title,
        ContactFirstName: values.siteContactDetails.contactFirstName,
        ContactSurname: values.siteContactDetails.contactSurname,
        EmailAddress: values.siteContactDetails.emailAddress,
        ContactNumber: values.siteContactDetails.contactNumber,
        MobileNumber: values.siteContactDetails.secondaryContactNumber
      },
      AddressDetails: {
        AddressName: values.siteAddress.addressName,
        AddressLine1: values.siteAddress.addressLine1,
        AddressLine2: values.siteAddress.addressLine2,
        AddressLine3: values.siteAddress.addressLine3,
        Town: values.siteAddress.town,
        County: values.siteAddress.county,
        PostCode: values.siteAddress.postCode
      },
      JobComments: values.jobComments
    };

    setShowUpdateWorkflow(false);
    setShowLoadingModal(true);
    const response = await axios.patch(`${EnvironmentVariables.ApiUrl}/jbp/${mpan}/booking/${bookingId}/${contractId}`, formattedValues, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'Contract-Type': contractTypeCodes[serviceLine],
        MPID: site?.userMpid ?? (userAssociatedMpids ? userAssociatedMpids.join(',') : ''),
        'Contract-ID': contractId
      }
    });

    if (response.status === 200) {
      onEditJobSuccess();
    } else {
      onEditJobError();
    }
  };

  return (
    <div>
      {
        <Button
          text="Edit Job"
          primary={true}
          onClick={() => {
            setShowUpdateWorkflow(true);
          }}
          disabled={false}
          widthStyling="w-full"
          className="mt-2 inline-block"
        />
      }
      {showLoadingModal ? (
        <Modal title={modalTitle}>
          <Typography content={`Please wait whilst your job update is processed`} className="mb-3 loading" />
          <span className="pl-10"></span>
        </Modal>
      ) : (
        <span></span>
      )}
      {showUpdateWorkflow ? (
        <FormModal title={`${modalTitle} - ${bookingId}`}>
          <FormWorkflowView<EditJobFormValues>
            onSubmit={handSubmit}
            initialValues={{
              siteAddress: {
                addressName: booking.AddressDetails.AddressName ?? '',
                addressLine1: booking.AddressDetails.AddressLine1 ?? '',
                addressLine2: booking.AddressDetails.AddressLine2 ?? '',
                addressLine3: booking.AddressDetails.AddressLine3 ?? '',
                town: booking.AddressDetails.Town ?? '',
                county: booking.AddressDetails.County ?? '',
                postCode: booking.AddressDetails.PostCode ?? ''
              },
              siteContactDetails: {
                title: booking.SiteContactDetails?.Title ?? '',
                contactFirstName: booking.SiteContactDetails?.ContactFirstName ?? '',
                contactSurname: booking.SiteContactDetails?.ContactSurname ?? '',
                emailAddress: booking.SiteContactDetails?.EmailAddress ?? '',
                contactNumber: booking.SiteContactDetails?.ContactNumber ?? ''
              },
              jobComments: booking.JobComments ?? ''
            }}
            nodes={[
              {
                element: (
                  <Title
                    className="mt-7 mb-5 font-italic text-sm"
                    text="Please be advised updates to address details below will not update the details held in our portfolio. To do so please submit a D0302 flow file"
                  />
                ),
                indicatorType: IndicatorType.Info
              },
              siteAddressNode,
              siteContactDetailsNode,
              jobCommentsNode,
              {
                element: (
                  <div className="mt-4">
                    <FormButton type="submit" primary icon>
                      Confirm
                    </FormButton>
                  </div>
                )
              }
            ]}
          />
          <Button
            text="X"
            primary={false}
            onClick={() => {
              setShowUpdateWorkflow(false);
            }}
            disabled={false}
            className="text-center absolute right-0 top-0"
          />
        </FormModal>
      ) : (
        <span></span>
      )}
      {showSuccessModal ? (
        <Modal title={modalTitle}>
          <Typography content={`The Job has been updated successfully`} className="mb-3" />
          <Button
            primary={false}
            text="OK"
            className="mr-4 mt-5"
            disabled={false}
            onClick={() => {
              handleJobSuccess();
            }}
          />
        </Modal>
      ) : (
        <span></span>
      )}
      {showErrorModal ? (
        <Modal title={modalTitle}>
          <Typography content={`Something went wrong, please try again later`} className="mb-3" />
          <Button primary={false} text="OK" className="mr-4 mt-5" disabled={false} onClick={() => setShowErrorModal(false)} />
        </Modal>
      ) : (
        <span></span>
      )}
    </div>
  );
};
